import { useState } from 'react'
import { Modal, message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Commission } from '../types/commission.types'
import { commissionsService } from '../../../shared/api/services/commissions.service'

export const useCommissionActions = (refetch?: () => void) => {
    const queryClient = useQueryClient()
    const [selectedCommission, setSelectedCommission] = useState<Commission | null>(null)
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingCommission, setEditingCommission] = useState<Commission | null>(null)

    const createMutation = useMutation({
        mutationFn: commissionsService.createCommission,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['commissions'] })
            if (refetch) refetch()
            message.success('Commission created successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.error?.message || 'Failed to create commission')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => 
            commissionsService.updateCommission(id, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['commissions'] })
            if (refetch) refetch()
            message.success('Commission updated successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.error?.message || 'Failed to update commission')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: commissionsService.deleteCommission,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['commissions'] })
            if (refetch) refetch()
            message.success('Commission deleted successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.error?.message || 'Failed to delete commission')
        },
    })

    const handleViewDetails = (commission: Commission) => {
        setSelectedCommission(commission)
        setIsDetailDrawerOpen(true)
    }

    const handleAddCommission = () => {
        setEditingCommission(null)
        setIsFormModalOpen(true)
    }

    const handleEditCommission = (commission: Commission) => {
        setEditingCommission(commission)
        setIsFormModalOpen(true)
    }

    const handleDeleteCommission = (commission: Commission) => {
        Modal.confirm({
            title: 'Delete Commission',
            content: `Are you sure you want to delete commission for ${commission.productName}?`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(parseInt(commission.id))
            },
        })
    }

    const handleFormSubmit = (values: any) => {
        // Transform camelCase to snake_case for API
        const transformedData: any = {
            scope: values.scope,
            commission_type: values.commissionType,
            commission_value: values.commissionValue,
            status: values.status || 'active',
            priority: values.priority,
        }

        // Add scope-specific fields
        if (values.scope === 'product' && values.productIds) {
            transformedData.product_ids = values.productIds
        } else if (values.scope === 'category' && values.categoryId) {
            transformedData.category_id = values.categoryId
        } else if (values.scope === 'seller' && values.sellerId) {
            transformedData.seller_id = values.sellerId
        } else if (values.scope === 'campaign' && values.campaignId) {
            transformedData.campaign_id = values.campaignId
        }

        // Add optional slabs
        if (values.minOrderValue != null) transformedData.min_order_value = values.minOrderValue
        if (values.maxOrderValue != null) transformedData.max_order_value = values.maxOrderValue
        if (values.minQuantity != null) transformedData.min_quantity = values.minQuantity
        if (values.maxQuantity != null) transformedData.max_quantity = values.maxQuantity
        
        // Format dates to ISO strings
        if (values.effectiveFrom) {
            transformedData.effective_from = values.effectiveFrom.toISOString()
        }
        if (values.effectiveTo) {
            transformedData.effective_to = values.effectiveTo.toISOString()
        }
        
        if (editingCommission) {
            updateMutation.mutate({
                id: parseInt(editingCommission.id),
                data: transformedData,
            })
        } else {
            createMutation.mutate(transformedData)
        }
    }

    const closeDrawer = () => setIsDetailDrawerOpen(false)
    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        selectedCommission,
        isDetailDrawerOpen,
        isFormModalOpen,
        editingCommission,
        handleViewDetails,
        handleAddCommission,
        handleEditCommission,
        handleDeleteCommission,
        handleFormSubmit,
        closeDrawer,
        closeFormModal
    }
}
